import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { FormattedMessage, FormattedDuration, FormattedList, FormattedReactMessage } from 'LearningCenterUI/components/I18n';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import UIIcon from 'UIComponents/icon/UIIcon';
import { FLINT, LORAX } from 'HubStyleTokens/colors';
import { useSaveFavoriteTrack } from '../hooks/useSaveFavoriteContent';
import InProgressDot from './InProgressDot';
import PositionedInlineList from './PositionedInlineList';
import CompletedIcon from './CompletedIcon';
import ReducedCardLayout from './ReducedCardLayout'; // @ts-expect-error untyped import

import TrackDeprecatedDescription from '../../../TrackCard/TrackDeprecatedDescription';
import { getOverviewModalUrl, OVERVIEW_MODAL_PLAYLIST_PARAM } from '../../../../utils/RoutingUtils'; // @ts-expect-error untyped import

import useFilterParams from '../../../../hooks/useFilterParams';
import { useCallback } from 'react';
import UsageTracker from '../../../../utils/UsageTracker';
import { useAssetStatus, useRemainingLessons, useTrackAwardProgress } from '../../../../features/userProgress';
import { FILTER_CONSTS } from '../../../../constants/TopFilterConstants';
import { LikeLink } from '../../../AcademyLinks';
import { useIsOverview, useTrack, useTrackProgressTime } from '../../../../features/tracks';
import ReducedCardSkeleton from './ReducedCardSkeleton';
import { CardMenuIcon, ReducedCardMenuIconBox } from '../../../../features/cardMenu';
const CertificateIcon = styled(props => /*#__PURE__*/_jsx(UIIcon, Object.assign({
  color: LORAX,
  name: "certification",
  size: 18
}, props))).withConfig({
  displayName: "ReducedTrackCard__CertificateIcon",
  componentId: "sc-2gp8mp-0"
})(["margin-left:4px;position:relative;bottom:-2px;"]);
const GhostContainer = styled.div.withConfig({
  displayName: "ReducedTrackCard__GhostContainer",
  componentId: "sc-2gp8mp-1"
})(["color:", ";"], FLINT);

const ReducedTrackCard = ({
  trackId,
  use,
  onClick = () => {},
  appearsIn,
  position,
  isSelected,
  language,
  size,
  customAction,
  playlistId
}) => {
  const {
    track,
    trackLoading
  } = useTrack(trackId, language);
  const {
    url
  } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const isOverview = useIsOverview();
  const {
    search
  } = location;
  const isCertificateCourse = track && track.certification;
  const numLessons = track && track.lessonIds.length;
  const deprecated = track && track.deprecated;
  const {
    status,
    allProgressLoading
  } = useAssetStatus({
    id: Number(trackId),
    type: 'TRACK'
  });
  const {
    remainingTime,
    totalTime,
    trackProgressTimeLoading
  } = useTrackProgressTime(Number(trackId));
  const {
    totalLessons,
    remainingLessons
  } = useRemainingLessons({
    lessonIds: track ? track.lessonIds : []
  });
  const isCompleted = status === FILTER_CONSTS.STATUSES.COMPLETED;
  const isInProgress = status === FILTER_CONSTS.STATUSES.IN_PROGRESS;
  const isStarted = status !== FILTER_CONSTS.STATUSES.NOT_STARTED;
  const {
    award
  } = useTrackAwardProgress(trackId);
  const awardName = award && award.awardName;
  const progressAmount = isCompleted ? 1 : isInProgress ? (totalTime - remainingTime) / totalTime : 0;
  const [isSaved, onSaveClick] = useSaveFavoriteTrack({
    trackId
  });
  const noTrailingUrl = url.length > 0 && url[url.length - 1] === '/' ? url.replace(/\/$/, '') : url;

  const openAwardModal = e => {
    e.preventDefault();
    const link = `${noTrailingUrl}/award/${trackId}/${awardName}${search}`;
    history.push(link);
  };

  const filterParams = useFilterParams();
  const handleClick = useCallback(() => {
    onClick();
    UsageTracker.track('interaction', {
      action: 'content-card-click',
      contentType: 'TRACK',
      trackId,
      appearsIn,
      position,
      name: track && track.name,
      filters: JSON.stringify(filterParams),
      style: 'reduced',
      isInProgress,
      isCertification: isCertificateCourse,
      playlistId
    });
  }, [onClick, trackId, appearsIn, position, track, filterParams, isInProgress, isCertificateCourse, playlistId]);

  if (trackLoading || allProgressLoading || trackProgressTimeLoading) {
    return /*#__PURE__*/_jsx(ReducedCardSkeleton, {
      size: size,
      use: use
    });
  }

  if (!trackLoading && !track) {
    return null;
  }

  const params = [];
  const progressedTrack = isStarted && !isOverview && !playlistId;
  if (playlistId) params.push(`playlistId=${playlistId}`);
  if (progressedTrack && language) params.push(`language=${language}`);
  const searchParams = new URLSearchParams(search);
  if (playlistId) searchParams.set(OVERVIEW_MODAL_PLAYLIST_PARAM, playlistId.toString());
  return /*#__PURE__*/_jsx(ReducedCardLayout, {
    contentId: trackId,
    customAction: customAction,
    deprecated: deprecated,
    description: track.description,
    isCertificateCourse: isCertificateCourse,
    isCompleted: isCompleted,
    isSaved: isSaved,
    isSelected: isSelected,
    name: track.name,
    onClick: handleClick,
    onSaveClick: onSaveClick,
    progressAmount: progressAmount,
    rightAction: /*#__PURE__*/_jsx(ReducedCardMenuIconBox, {
      className: "m-right-3",
      children: /*#__PURE__*/_jsx(CardMenuIcon, {
        language: language,
        trackId: trackId,
        use: use
      })
    }),
    size: size,
    thumbnailUrl: track.previewThumbnailSquareUrl || '',
    to: progressedTrack ? {
      pathname: `/tracks/${trackId}`,
      search: params.join('&')
    } : Object.assign({}, location, {
      pathname: getOverviewModalUrl(location.pathname, true, trackId, appearsIn, language),
      search: searchParams.toString()
    }),
    use: use,
    children: deprecated ? /*#__PURE__*/_jsx(GhostContainer, {
      children: /*#__PURE__*/_jsx(TrackDeprecatedDescription, {
        compressed: true,
        cta: track.deprecationCta,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.reducedContentCards.notAvailable"
        }),
        trackId: trackId
      })
    }) : isCompleted ? /*#__PURE__*/_jsxs(PositionedInlineList, {
      children: [/*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(CompletedIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.reducedContentCards.completed"
        })]
      }), isCertificateCourse && /*#__PURE__*/_jsx(LikeLink, {
        className: "certificate-link",
        onClick: e => openAwardModal(e),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.reducedContentCards.viewCertificate"
        })
      })]
    }) : isInProgress ? /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx(InProgressDot, {}), /*#__PURE__*/_jsxs(FormattedList, {
        exclusive: true,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.reducedContentCards.numLessonsRemaining",
          options: {
            remainingNumber: remainingLessons,
            totalNumber: totalLessons
          }
        }), /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.reducedContentCards.timeRemaining",
          options: {
            time: /*#__PURE__*/_jsx(FormattedDuration, {
              format: "medium",
              from: +new Date() - remainingTime * 1000
            })
          }
        })]
      })]
    }) : isCertificateCourse ? /*#__PURE__*/_jsxs(PositionedInlineList, {
      children: [/*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.reducedContentCards.certificate"
        }), /*#__PURE__*/_jsx(CertificateIcon, {})]
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.reducedContentCards.numLessons",
        options: {
          number: numLessons
        }
      }), /*#__PURE__*/_jsx(FormattedDuration, {
        format: "medium",
        from: +new Date() - totalTime * 1000
      })]
    }) : /*#__PURE__*/_jsxs(PositionedInlineList, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.reducedContentCards.course"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.reducedContentCards.numLessons",
        options: {
          number: numLessons
        }
      }), /*#__PURE__*/_jsx(FormattedDuration, {
        format: "medium",
        from: +new Date() - totalTime * 1000
      })]
    })
  });
};

export default ReducedTrackCard;